import { useLayoutEffect, useMemo, useState } from "react";
import { useThemeSlice } from "../../../components/ui/StatusBadge.jsx";
import { Ico } from "../../../components/icons/icons.jsx";
import { ModalShellHeader } from "../../../components/modal/ModalShellHeader.jsx";
import { modalPrimaryButton } from "../../../components/ui/buttonStyles.js";
import { buildModalShellTokens } from "../../../components/modal/modalShellTheme.js";
import {
  EMPTY_ACCOUNT_FORM,
  STEP_SUBTITLES,
  accountToForm,
  formToAccountPayload,
  validateAccountStep,
} from "../accountFormConfig.js";
import { AccountModalStepCompany } from "./AccountModalStepCompany.jsx";
import { AccountModalStepAccount } from "./AccountModalStepAccount.jsx";
import { AccountModalStepContact } from "./AccountModalStepContact.jsx";

const TOTAL_STEPS = 3;

export function CreateAccountModal({ open, onClose, onSave, editing = null, formKey = 0, saving = false }) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const ap = useMemo(() => buildModalShellTokens(dark, c, A), [dark, c, A]);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ ...EMPTY_ACCOUNT_FORM });
  const [errors, setErrors] = useState({});

  useLayoutEffect(() => {
    if (!open) return;
    setStep(1);
    setForm(accountToForm(editing));
    setErrors({});
  }, [open, editing, formKey]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleNext = () => {
    const nextErrors = validateAccountStep(form, step);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  };

  const handleBack = () => {
    setErrors({});
    if (step === 1) {
      onClose();
      return;
    }
    setStep((s) => s - 1);
  };

  const handleSubmit = async () => {
    await onSave(formToAccountPayload(form, editing));
    onClose();
  };

  if (!open) return null;

  const modalWidth = step === 2 ? "min(920px, 96vw)" : 620;
  const isEdit = Boolean(editing);

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, zIndex: 70, background: ap.overlay, backdropFilter: "blur(8px)" }}
        role="presentation"
      />
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-modal-title"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: modalWidth,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: 16,
          background: ap.cardGradient,
          border: `1px solid ${ap.cardBorder}`,
          boxShadow: ap.cardShadowLg,
          zIndex: 71,
        }}
      >
        <ModalShellHeader
          title={isEdit ? "Edit account" : "Create account"}
          titleId="account-modal-title"
          subtitle={STEP_SUBTITLES[step]}
          icon={isEdit ? Ico.pencil : Ico.plus}
          onClose={onClose}
          closeLabel="Close account form"
          dark={dark}
          c={c}
          A={A}
          ap={ap}
          trailing={(
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: A, background: `${A}18`, border: `1px solid ${A}33`, borderRadius: 999, padding: "6px 10px" }}>
              {step} / {TOTAL_STEPS}
            </div>
          )}
        />

        <div style={{ padding: 24, overflowY: "auto", flex: 1, minHeight: 0 }}>
          {step === 1 && <AccountModalStepCompany form={form} set={set} errors={errors} />}
          {step === 2 && <AccountModalStepAccount form={form} set={set} />}
          {step === 3 && <AccountModalStepContact form={form} set={set} />}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <button type="button" onClick={handleBack} disabled={saving} style={outlineBtn(c, dark)}>
              {step === 1 ? "Cancel" : "Back"}
            </button>
            {step < TOTAL_STEPS ? (
              <button type="button" onClick={handleNext} style={modalPrimaryButton(A, dark)}>
                Next
              </button>
            ) : (
              <button type="button" onClick={handleSubmit} disabled={saving} style={modalPrimaryButton(A, dark, saving)}>
                {saving ? "Saving…" : isEdit ? "Save changes" : "Create account"}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function outlineBtn(c, dark) {
  return {
    height: 44,
    padding: "0 20px",
    borderRadius: 10,
    border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
    background: "transparent",
    color: c.text,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
  };
}

